/* Production — one day in detail, or the month.
 *
 * Daily is the screen the mill actually opens: a strip of the month's days
 * coloured by efficiency, then that day's four figures, every item it ran,
 * the meters, where the time went, and each item's output against its
 * target as a filled bar inside an outlined one. Month is the same figures
 * rolled up, with the calendar and a bar per day.
 *
 * Efficiency here is output against the SITRA standard, which is the
 * headline everywhere else in the app; a bar or a cell goes to the ink
 * colour rather than the accent when it is under the benchmark.
 */

import { Blueprint } from '../../components/ui'
import type { MobileSummary } from '../../lib/api_mobile'
import { MobileTable, type MCol, type MRow } from '../MobileTable'
import {
  BAD, Bars, Calendar, DayStrip, Head, INK, IN, KG, KpiGrid, NoteBox, P1, SG, Seg, TN,
  kpi, type CalDay, type MBar,
} from '../parts'

export type ProdTab = 'day' | 'month'

export function Production({ data, tab, onTab, day, onDay, dark }: {
  data: MobileSummary
  tab: ProdTab
  onTab: (t: ProdTab) => void
  /** Index into prod.days. */
  day: number
  onDay: (i: number) => void
  dark: boolean
}) {
  const days = data.prod.days
  const bench = data.bench
  const cal: CalDay[] = days.map((x) => ({ d: x.d, v: x.e, dow: x.dow }))
  const sumA = days.reduce((s, x) => s + x.a, 0)
  const sumT = days.reduce((s, x) => s + x.t, 0)
  const mEff = sumT ? sumA / sumT * 100 : 0
  const short = data.label.slice(0, 3)

  return (
    <>
      <Seg value={tab} onChange={onTab}
        options={[{ v: 'day', l: 'Daily report' }, { v: 'month', l: 'Month' }]} />
      <div className="m-label" style={{ padding: '12px 0 2px' }}>
        {data.label} · Unit 1 + Unit 2 · {data.prod.machines} machines
      </div>

      {tab === 'day'
        ? <Day data={data} index={day} onDay={onDay} dark={dark} />
        : (
          <Month data={data} dark={dark} day={day} onDay={(i) => { onDay(i); onTab('day') }}
            sumA={sumA} sumT={sumT} mEff={mEff} cal={cal} short={short} bench={bench} />
        )}
    </>
  )
}

// ----------------------------------------------------------------- a day
function Day({ data, index, onDay, dark }: {
  data: MobileSummary
  index: number
  onDay: (i: number) => void
  dark: boolean
}) {
  const days = data.prod.days
  const d = days[index]
  const bench = data.bench
  if (!d) return <NoteBox>No day on file for this month.</NoteBox>

  const items = d.items.slice().sort((a, b) => b.a - a.a)
  const maxT = Math.max(1, ...d.items.map((i) => Math.max(i.t, i.a)))

  const itemCols: MCol[] = [
    { l: 'Item', w: 128 }, { l: 'Unit', w: 50 }, { l: 'M/cs', w: 50 },
    { l: 'Actual', w: 76 }, { l: 'Target', w: 76 }, { l: 'Eff', w: 92 },
    { l: 'GPS a / t', w: 92 }, { l: 'Waste', w: 66 },
  ]
  const itemRows: MRow[] = d.items.map((i) => {
    const e = i.t ? i.a / i.t * 100 : 0
    return {
      cells: [
        { v: i.c, sub: i.q },
        { v: i.u },
        { v: String(i.m) },
        { v: IN(i.a) },
        { v: IN(i.t) },
        {
          v: P1(e), bar: e, s: e,
          barC: e < bench ? 'var(--c-ink)' : 'var(--c-bar1)',
          color: e < bench ? BAD : INK,
        },
        { v: `${i.ag ?? '–'} / ${i.tg ?? '–'}`, s: i.ag ?? 0 },
        { v: IN(i.w) },
      ],
    }
  })

  const stopCols: MCol[] = [{ l: 'Reason', w: 150 }, { l: 'Unit 1', w: 90 }, { l: 'Unit 2', w: 90 }]
  const stopRows: MRow[] = d.stop.map((s) => ({
    cells: [
      { v: s.c },
      { v: s.u1 == null ? '–' : `${s.u1}%`, s: s.u1 ?? -1 },
      { v: s.u2 == null ? '–' : `${s.u2}%`, s: s.u2 ?? -1 },
    ],
  }))

  const power = [
    { l: 'EB units', v: IN(d.eb) },
    { l: 'Solar units', v: IN(d.solar) },
    { l: 'UKG', v: d.ukg == null ? '–' : String(d.ukg) },
  ]

  return (
    <>
      <DayStrip days={days.map((x) => ({ d: x.d, v: x.e, dow: x.dow }))}
        bench={bench} dark={dark} selected={index} onPick={onDay} />
      <h2 className="m-h2" style={{ fontSize: 24, margin: '18px 0 0' }}>
        {d.dow} {d.d} {data.label}
      </h2>

      <KpiGrid style={{ marginTop: 16 }} items={[
        kpi('Production', KG(d.a), `of ${KG(d.t)} target`),
        kpi('Efficiency', P1(d.e),
          `${SG(d.e - bench, (x) => x.toFixed(1))} pts vs ${bench}% bench`, d.e < bench),
        kpi('Unit 1 / Unit 2', `${d.u1 ?? '–'} / ${d.u2 ?? '–'}`, 'efficiency, %',
          (d.u1 != null && d.u1 < bench) || (d.u2 != null && d.u2 < bench)),
        kpi('Waste', KG(d.w), d.a ? `${P1(d.w / d.a * 100)} of output` : 'no output booked'),
      ]} />

      <Head title="Items run" aside={`${d.items.length} lines`} />
      <MobileTable cols={itemCols} rows={itemRows} />

      <Head title="Power" aside={d.eb == null ? 'no reading' : undefined} />
      <Blueprint style={{
        padding: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
      }}>
        {power.map((p, i) => (
          <div key={p.l} style={{
            padding: 12,
            borderRight: i < 2 ? '1px solid var(--color-divider)' : undefined,
          }}>
            <div className="m-label">{p.l}</div>
            <div className="m-num" style={{
              fontFamily: 'var(--font-heading)', fontSize: 21, marginTop: 6,
            }}>{p.v}</div>
          </div>
        ))}
      </Blueprint>

      {d.stop.length > 0 && (
        <>
          <Head title="Where the time went" aside="% of machine minutes" />
          <MobileTable cols={stopCols} rows={stopRows} />
        </>
      )}

      <Head title="Output against target, by item" />
      <div className="m-aside" style={{ marginTop: -6, marginBottom: 12 }}>
        Outline is target, fill is actual
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {items.map((i, n) => {
          const e = i.t ? i.a / i.t * 100 : 0
          return (
            <div key={`${i.n}-${i.u}-${n}`} style={{
              display: 'flex', flexDirection: 'column', gap: 5,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5,
              }}>
                <span style={{ fontWeight: 600 }}>
                  {i.c} <span style={{ fontWeight: 400, color: 'var(--color-neutral-600)' }}>
                    {i.u} · {i.m} m/c
                  </span>
                </span>
                <span className="m-num" style={{ color: e < bench ? BAD : 'var(--color-neutral-700)' }}>
                  {P1(e)}
                </span>
              </div>
              <div style={{ position: 'relative', height: 16 }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${i.t / maxT * 100}%`,
                  border: '1px solid var(--c-bar1)', boxSizing: 'border-box',
                }} />
                <div style={{
                  position: 'absolute', left: 0, top: 4, bottom: 4,
                  width: `${i.a / maxT * 100}%`,
                  background: e < bench ? 'var(--c-ink)' : 'var(--c-bar1)',
                }} />
              </div>
              <div className="m-num" style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                {KG(i.a)} of {KG(i.t)}
              </div>
            </div>
          )
        })}
      </div>
      <div className="m-aside" style={{ marginTop: 14 }}>
        Waste is booked for the plant by department, so each item's share of
        it is its share of the day's output — not a separate reading.
      </div>
    </>
  )
}

// --------------------------------------------------------------- the month
function Month({ data, dark, day, onDay, sumA, sumT, mEff, cal, bench }: {
  data: MobileSummary
  dark: boolean
  day: number
  onDay: (i: number) => void
  sumA: number
  sumT: number
  mEff: number
  cal: CalDay[]
  short: string
  bench: number
}) {
  const days = data.prod.days
  if (!days.length) return <NoteBox>No days on file for this month.</NoteBox>

  const best = days.reduce((m, d) => (d.e > m.e ? d : m))
  const worst = days.reduce((m, d) => (d.e < m.e ? d : m))
  const below = days.filter((d) => d.e < bench).length
  const maxT = Math.max(1, ...days.map((d) => Math.max(d.t, d.a)))

  const bars: MBar[] = days.map((d, i) => ({
    h: d.a / maxT * 100,
    th: d.t / maxT * 100,
    c: d.e < bench ? 'var(--c-ink)' : 'var(--c-bar1)',
    sel: i === day,
    lab: d.d % 5 === 0 || d.d === 1 ? String(d.d) : '',
    on: () => onDay(i),
  }))

  // Items rolled up over the month, and the two units against the plant.
  const agg = new Map<string, {
    c: string; q: string; u: string; a: number; t: number; w: number; m: number; n: number
  }>()
  const units = new Map<string, { a: number; t: number; w: number }>()
  days.forEach((d) => d.items.forEach((i) => {
    const key = `${i.c}|${i.q}|${i.u}`
    const row = agg.get(key)
      ?? { c: i.c, q: i.q, u: i.u, a: 0, t: 0, w: 0, m: 0, n: 0 }
    row.a += i.a; row.t += i.t; row.w += i.w; row.m += i.m; row.n += 1
    agg.set(key, row)
    const u = units.get(i.u) ?? { a: 0, t: 0, w: 0 }
    u.a += i.a; u.t += i.t; u.w += i.w
    units.set(i.u, u)
  }))
  const items = [...agg.values()].sort((a, b) => b.a - a.a)
  const maxA = items[0]?.a || 1

  const itemRows: MRow[] = items.map((i) => {
    const e = i.t ? i.a / i.t * 100 : 0
    return {
      cells: [
        { v: i.c, sub: i.q },
        { v: i.u },
        { v: IN(i.a), bar: i.a / maxA * 100, s: i.a },
        { v: IN(i.t) },
        { v: P1(e), s: e, color: e < bench ? BAD : INK },
        { v: String(i.n) },
        { v: (i.m / i.n).toFixed(1) },
        { v: IN(i.w) },
      ],
    }
  })

  const unitRow = (label: string, x: { a: number; t: number; w: number }): MRow => {
    const e = x.t ? x.a / x.t * 100 : 0
    return {
      cells: [
        { v: label },
        { v: TN(x.a), s: x.a },
        { v: TN(x.t) },
        { v: P1(e), s: e, color: e < bench ? BAD : INK },
        { v: TN(x.w) },
        { v: x.a ? P1(x.w / x.a * 100) : '–' },
      ],
    }
  }
  const plant = { a: sumA, t: sumT, w: days.reduce((s, d) => s + d.w, 0) }

  const { eb, solar, ukg, meteredDays } = data.prod.power

  return (
    <>
      <KpiGrid style={{ marginTop: 16 }} items={[
        kpi('Production', TN(sumA), `of ${TN(sumT)} target`),
        kpi('Efficiency', P1(mEff),
          `below ${bench}% on ${below} of ${days.length} days`, mEff < bench),
        kpi('Best day', P1(best.e), `${best.dow} ${best.d} ${data.label.slice(0, 3)}`),
        kpi('Worst day', P1(worst.e), `${worst.dow} ${worst.d} ${data.label.slice(0, 3)}`, true),
      ]} />

      <Head title={`Efficiency · ${data.label.split(' ')[0]}`} aside="tap a day" />
      <Calendar days={cal} bench={bench} dark={dark} selected={day} onPick={onDay} />

      <Head title="Daily output" aside="kg · tap a day" />
      <div className="m-aside" style={{ marginTop: -6, marginBottom: 12 }}>
        Outline is target; dark fill is a day below bench
      </div>
      <Bars bars={bars} gap={2} legend={[
        { t: `At or above ${bench}%`, c: 'var(--c-bar1)' },
        { t: `Below ${bench}%`, c: 'var(--c-ink)' },
        { t: 'Target', border: '1px solid var(--m-target)' },
      ]} />

      <Head title="Monthly summary · by item" aside="highest output first" />
      <MobileTable cols={[
        { l: 'Item', w: 128 }, { l: 'Unit', w: 50 }, { l: 'Actual kg', w: 116 },
        { l: 'Target kg', w: 86 }, { l: 'Eff', w: 70 }, { l: 'Days', w: 50 },
        { l: 'Avg m/c', w: 64 }, { l: 'Waste kg', w: 76 },
      ]} rows={itemRows} />

      <Head title="Monthly summary · by unit" aside={`${days.length} days`} />
      <MobileTable cols={[
        { l: 'Unit', w: 80 }, { l: 'Actual', w: 72 }, { l: 'Target', w: 72 },
        { l: 'Eff', w: 64 }, { l: 'Waste', w: 64 }, { l: 'Waste %', w: 66 },
      ]} rows={[
        ...(units.has('U1') ? [unitRow('Unit 1', units.get('U1')!)] : []),
        ...(units.has('U2') ? [unitRow('Unit 2', units.get('U2')!)] : []),
        unitRow('Plant', plant),
      ]} />

      <NoteBox>
        Power · {data.label}: {IN(eb)} EB units and {IN(solar)} solar units
        {eb + solar > 0 ? ` (${P1(solar / (eb + solar) * 100)} solar)` : ''}
        {ukg == null
          ? '. No meter readings on file, so there is no figure per kilo.'
          : `, ${ukg} units per kg of yarn over the ${meteredDays} metered ${meteredDays === 1 ? 'day' : 'days'}.`}
      </NoteBox>
    </>
  )
}
